import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useModal from 'hooks/useModal/useModal';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { EditExternalUserDrawer, ResendInvitationModalForm } from 'state/types';
import Button from 'ui/atoms/Button/Button';
import Icon from 'ui/atoms/Icon/Icon';
import Modal from 'ui/atoms/Modal/Modal';
import Typography from 'ui/atoms/Typography/Typography';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const ResendInvitation: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { hideModal, modalState } = useModal();
  const { showDrawer, drawerState } = useDrawer<EditExternalUserDrawer>();
  const enqueueSnackbar = useEnqueueSnackbar();
  const modalStateInitialValue: ResendInvitationModalForm = modalState?.contentValue;
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    sendInvitationForMobileDeviceById,
    sendInvitationForMobileDeviceByIdMutationLoading,
    unregisterMobileDeviceById,
    unregisterMobileDeviceByIdMutationLoading
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const mobileDevice = modalStateInitialValue?.mobileDevices.find(
    (device) => device.role === modalStateInitialValue?.type
  );

  const handleResendInvitation = () => {
    if (mobileDevice && !sendInvitationForMobileDeviceByIdMutationLoading) {
      if (!mobileDevice.isRegistered) {
        sendInvitationForMobileDeviceById(modalStateInitialValue.visitorId, mobileDevice.id)
          .then((data) => {
            if (data?.sendInvitationForMobileDeviceById) {
              enqueueSnackbar(
                `${modalStateInitialValue?.type.charAt(0) + modalStateInitialValue?.type.slice(1).toLowerCase()} ${
                  translation.invitation_successfully_send
                }`,
                {
                  snackbartype: 'success'
                }
              );
            }
          })
          .finally(hideModal);
      } else if (!unregisterMobileDeviceByIdMutationLoading) {
        unregisterMobileDeviceById(modalStateInitialValue.visitorId, mobileDevice.id)
          .then(() =>
            showDrawer({
              ...drawerState,
              contentValue: {
                ...drawerState.contentValue,
                mobileDevices: drawerState.contentValue.mobileDevices.map((mobileDeviceDrawer) =>
                  mobileDeviceDrawer.id === mobileDevice.id
                    ? {
                        ...mobileDeviceDrawer,
                        isRegistered: false,
                        registrationDate: null,
                        registrationToken: null
                      }
                    : mobileDeviceDrawer
                )
              }
            })
          )
          .finally(hideModal);
      }
    }
  };

  return (
    <Modal>
      <ComponentWrapper
        flexDirection="column"
        width="36rem"
        gap="2rem"
        margin="1rem 0"
        justifyContent="center"
        alignItems="center"
      >
        <Icon name="SendEmail" color="dTextHigh" width={40} height={40} viewBox="0 0 24 24" id="delete-bin-icon" />
        <ComponentWrapper flexDirection="column" justifyContent="center" alignItems="center">
          <ComponentWrapper flexDirection="column" justifyContent="center" alignItems="center">
            <Typography variant="title2" color="dTextHigh">
              {`${
                translation.are_you_sure_that_you_want_to_resend_the
              } ${modalStateInitialValue?.type.toLocaleLowerCase()} ${translation.invitation_e_mail}?`}
            </Typography>
          </ComponentWrapper>
        </ComponentWrapper>
        <ComponentWrapper width="100%">
          <Button backgroundColor="transparent" textColor="b1" onClick={hideModal} id="cancel-button-modal">
            {translation.no_thank_you}
          </Button>
          <Button onClick={handleResendInvitation} id="ok-button-modal">
            {translation.yes_i_m_sure}
          </Button>
        </ComponentWrapper>
      </ComponentWrapper>
    </Modal>
  );
};

export default ResendInvitation;
